import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/userProfile.ts';
import { User, Book, Brain, MessageSquare, Trophy, Edit } from 'lucide-react';
import { supabase } from '../lib/supabase.js';

function Dashboard() {
  const { user } = useAuth();
  const { profile, loading, updateProfile: updateProfileHook } = useProfile();
  const [quizStats, setQuizStats] = useState(null);
  const [recentChats, setRecentChats] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    if (profile?.username) {
      setUsername(profile.username);
    }
  }, [profile]);

  useEffect(() => {
    if (!user) return;

    const fetchStatsAndChats = async () => {
      try {
        const { data: quizData, error: quizError } = await supabase
          .from('quiz_scores')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (quizError) throw quizError;
        setQuizStats(quizData);

        const { data: chatData, error: chatError } = await supabase
          .from('chat_history')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5);

        if (chatError) throw chatError;
        setRecentChats(chatData);
      } catch (error) {
        console.error('Error fetching stats/chats:', error);
      }
    };

    fetchStatsAndChats();
  }, [user]);

  const handleUpdateProfile = async () => {
    const { error } = await updateProfileHook({ username: username.trim() });

    if (error) {
      alert(error);
    } else {
      setIsEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment pt-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-parchment-dark border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment pt-16 font-serif">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <User className="h-16 w-16 text-parchment-dark" />
          </div>
          <div className="flex items-center justify-center gap-2">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="vintage-input"
                  placeholder="Enter username"
                />
                <button onClick={handleUpdateProfile} className="vintage-button">
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setUsername(profile?.username || '');
                  }}
                  className="vintage-button"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <h1 className="vintage-title">
                  Welcome, {profile?.username || user.email?.split('@')[0]}
                </h1>
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-parchment-dark hover:text-parchment-dark/80"
                >
                  <Edit className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          <p className="vintage-subtitle max-w-3xl mx-auto">
            Track your progress and review your historical journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Quiz Statistics */}
          <div className="vintage-card">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="h-6 w-6 text-parchment-dark" />
              <h2 className="text-xl font-bold text-parchment-dark">Quiz Performance</h2>
            </div>
            {quizStats?.length > 0 ? (
              <div className="space-y-4">
                <div className="text-4xl font-bold text-parchment-dark">
                  {Math.round(
                    (quizStats.reduce((acc, quiz) => acc + (quiz.score / quiz.total_questions), 0) /
                      quizStats.length) *
                      100
                  )}%
                </div>
                <p className="text-parchment-dark/80">Average Score</p>
                <p className="text-parchment-dark/80">
                  Total Quizzes: {quizStats.length}
                </p>
              </div>
            ) : (
              <p className="text-parchment-dark/60 italic">No quiz attempts yet</p>
            )}
          </div>

          {/* Recent Chats */}
          <div className="vintage-card">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="h-6 w-6 text-parchment-dark" />
              <h2 className="text-xl font-bold text-parchment-dark">Recent Discussions</h2>
            </div>
            {recentChats?.length > 0 ? (
              <div className="space-y-4">
                {recentChats.map((chat) => (
                  <div key={chat.id} className="border-b border-parchment-dark/20 pb-2">
                    <p className="text-parchment-dark font-medium truncate">
                      {chat.message}
                    </p>
                    <p className="text-sm text-parchment-dark/60">
                      {new Date(chat.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-parchment-dark/60 italic">No chat history yet</p>
            )}
          </div>

          {/* Achievements */}
          <div className="vintage-card">
            <div className="flex items-center gap-3 mb-4">
              <Trophy className="h-6 w-6 text-parchment-dark" />
              <h2 className="text-xl font-bold text-parchment-dark">Achievements</h2>
            </div>
            <div className="space-y-4">
              {quizStats?.length > 0 && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-parchment-dark/10 rounded-full">
                    <Book className="h-5 w-5 text-parchment-dark" />
                  </div>
                  <div>
                    <p className="font-medium text-parchment-dark">Quiz Master</p>
                    <p className="text-sm text-parchment-dark/60">
                      Completed {quizStats.length} quizzes
                    </p>
                  </div>
                </div>
              )}
              {recentChats?.length >= 5 && (
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-parchment-dark/10 rounded-full">
                    <MessageSquare className="h-5 w-5 text-parchment-dark" />
                  </div>
                  <div>
                    <p className="font-medium text-parchment-dark">Engaged Learner</p>
                    <p className="text-sm text-parchment-dark/60">
                      Had {recentChats.length} historical discussions
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;